/**
 * App.jsx - PSP/CRE Tool v3.0
 * Main Application Component
 *
 * Author: Dr. Tatar Radu MD
 * Version: 3.3.0
 * Date: April 6, 2026
 *
 * CHANGELOG:
 * v3.3.0 (April 6, 2026) - V4-DM-001: Schema versioning.
 *   CURRENT_SCHEMA_VERSION = '3.1.0'. Added to newScenario template.
 *   Load-time migration: v3.0 saves without schema_version are backfilled to
 *   '3.0.0' and migrated to 3.1.0 (no structural changes needed — all V4
 *   fields are optional with null defaults, guarded by ?. in all read sites).
 *   Future field renames or structural changes must bump CURRENT_SCHEMA_VERSION
 *   and add a migration case. console.info fires once per migrated load.
 *
 * v3.2.2 (March 12, 2026):
 * - BUG-073 FIX: Purged all console.log statements from App.jsx
 *   - Removed console.log('Scenario saved successfully:', scenario) from handleSaveScenario
 *     ROOT CAUSE: This serialized the entire scenario object to DevTools on every auto-save
 *     (every scenario state change), causing 30-60s UI freezes on large scenarios
 *   - Removed console.log from stale scenario version check (load-time, low impact)
 *   - Removed 6× console.log statements from markTabComplete (fired on every Save & Continue)
 *   - KEPT: console.error in catch blocks (Save failed, AUTO-SAVE ERROR) — legitimate
 * - CRITICAL FIX: Calculator results now save to localStorage
 * - ISSUE: Tab4 calculated correct values but Tab5 showed old/missing data
 * - ROOT CAUSE: Auto-save useEffect (line 307-380) excluded casualty_projections from hash
 *   - When Tab4 called onUpdate() with calculator results, React state updated
 *   - But hash computed from ONLY user inputs (explicitly excludes calculator results)
 *   - Hash unchanged → early return without saving → calculator results stayed in memory only
 * - FIX: Added calculator timestamp tracking to force save when calculator runs
 *   - Lines 350-365: Check if calculator results present and timestamp changed
 *   - Forces save even when user input hash unchanged
 *   - lastSavedHashRef.calculatorTimestamp tracks when calculator last ran
 * - RESULT: Tab4's casualty_projections now persist to localStorage → Tab5 displays correctly
 *
 * v3.2.0 (February 10, 2026):
 * - ARCH: Toolbar navigation now calls flushState() on active tab before switching
 *   - Tabs 1-5 expose flushState() via useImperativeHandle / forwardRef
 *   - handleTabChange calls activeTabRef.current?.flushState() before setCurrentTab
 *   - Data is silently persisted to scenario via onUpdate when user clicks toolbar tab
 *   - Tab 6 (COA placeholder) exposes no-op flushState stub
 *   - tab1Ref..tab6Ref created with useRef, passed as ref= to each tab component
 *
 * v3.1.2 (February 10, 2026):
 * v3.1.2 (February 10, 2026):
 * - TAB-006: Added Tab 6 COA Analysis placeholder
 *   - Tab6_COAAnalysis.jsx wired into TAB_CONFIG, conditional render, and mode guard
 *   - Tab 6 requires Tab 5 completion before unlocking (correct dependency chain)
 *   - forwardRef + flushState stub included (ready for toolbar navigation)
 *   - Under Construction placeholder with full spec preview and example output
 *   - Short label: 'COA' in stepper toolbar
 *
 * v3.1.1 (February 10, 2026):
 * - BUG-067 FIX: Expert mode not applied when entering tabs via toolbar
 *   - ROOT CAUSE: BUG-066 fix (always-mounted tabs) caused all tabs to mount
 *     simultaneously on app start, at which point mode state was still null.
 *     Tabs initialising local state from the mode prop captured null, defaulting
 *     to standard view regardless of user selection.
 *   - AFTER: Wrapped all 5 tab Boxes in {mode && (...)} guard. Tabs only mount
 *     once mode is confirmed - they receive correct mode value on first render.
 *   - BUG-066 fix fully preserved: tabs stay mounted permanently after mode set.
 *
 * v3.1.0 (February 10, 2026):
 * - BUG-066 FIX: Toolbar navigation no longer loses unsaved tab data
 *   - BEFORE: Conditional rendering unmounted tabs on switch -> state lost
 *   - AFTER:  CSS display:none/block keeps all tabs mounted -> state preserved
 *   - Impact: User can freely navigate toolbar without losing any input
 * - FIX-004 FIX: Reset button now performs complete app reset without server restart
 *   - BEFORE: Reset cleared only completedTabs and currentTab UI state
 *   - BEFORE: Scenario data remained in memory and localStorage -> required server restart
 *   - AFTER:  localStorage.clear() + window.location.reload() = instant full reset
 *   - Impact: No more stop/restart server cycle during development or testing
 * - BUG-003 FIX: Version guard on localStorage load prevents v2.0 data corruption
 *   - BEFORE: Any saved scenario loaded regardless of version -> silent failures
 *   - AFTER:  Non-v3.0 scenarios cleared automatically on load
 *   - Impact: Stale v2.0 data can no longer silently break the session
 * - BUG-004 FIX: "Fix in Tab 4" dialog button used integer (3) instead of string ('tab4')
 *   - BEFORE: setCurrentTab(3) -> matched nothing -> navigation silently failed
 *   - AFTER:  setCurrentTab('tab4') -> correct navigation
 *   - Impact: Validation modal "Fix in Tab 4" button now works correctly
 *
 * v3.0.x (Previous):
 * - v2.9 FIX: Save on page refresh/close (beforeunload handler)
 * - v2.8 FIX: Scroll to top on tab change
 * - v2.7 FIX: Navigation always fires, not only on first completion
 * - CRIT-007: updateScenario passes only owned data, App.jsx merges
 * - PHASE 4.7.4: Pre-save validation gate (validateResupplyAdequacy)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Paper,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Alert,
  AlertTitle,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Snackbar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

// V4-005: i18n — initialise before any component renders.
// Language selector rendered in app header. Tabs 5 + 7 fully wired.
// Deferred: Tabs 1-4, 6 (follow-on sessions).
import './i18n/config.js';
import { useTranslation } from 'react-i18next';

// Import components
import WelcomeScreen from './components/WelcomeScreen';
import ModeSelector from './components/ModeSelector';
import ErrorBoundary from './components/ErrorBoundary';
import ScenarioToolbar from './components/ScenarioToolbar';

// Phase 2 — multi-user identity + role context
import SplashScreen from './components/SplashScreen';
import TrainerSettings from './components/TrainerSettings';       // P2-S6
import { UserRoleContext } from './context/UserRoleContext';
import { authService } from './services/authService';            // P2-S4 logout
import { exerciseService } from './services/exerciseService';    // co-organiser invite redemption
import { cloudStorageService } from './services/cloudStorageService'; // cloud scenario backup

// Import tabs with PascalCase aliases to satisfy ESLint react/jsx-pascal-case
import Tab1ForceComposition from './tabs/Tab1_ForceComposition';
import Tab2Operations from './tabs/Tab2_Operations';
import Tab3Medical from './tabs/Tab3_Medical';
import Tab4Logistics from './tabs/Tab4_Logistics';
import Tab5Reports from './tabs/Tab5_Reports';
import Tab6COAAnalysis from './tabs/Tab6_COAAnalysis';
import Tab7Exercise from './tabs/Tab7_Exercise';
import Tab8Debrief from './tabs/Tab8_Debrief';

// Import services
import StorageService from './services/StorageService';
import { repairScenarioForce } from './utils/forceRepair';

// PHASE 4.7.4: Import validation system
import { validateResupplyAdequacy } from './services/ValidationService';

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#2e7d32',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Tab configuration — IDs and requires are static; labels/descriptions resolved via i18n inside App()
const TAB_CONFIG_STATIC = [
  { id: 'tab1', requires: [] },
  { id: 'tab2', requires: ['tab1'] },
  { id: 'tab3', requires: ['tab1', 'tab2'] },
  { id: 'tab4', requires: ['tab1', 'tab2', 'tab3'] },
  { id: 'tab5', requires: ['tab1', 'tab2', 'tab3', 'tab4'] },
  { id: 'tab6', requires: ['tab1', 'tab2', 'tab3', 'tab4', 'tab5'] },
  { id: 'tab7', requires: ['tab1', 'tab2', 'tab3', 'tab4', 'tab5'] },
  { id: 'tab8', requires: ['tab7'] },
];

// V4-DM-001: Schema versioning — bump this when any structural field is added/renamed.
// Migration: load-time migrateScenario() backfills older saves to current schema.
const CURRENT_SCHEMA_VERSION = '3.1.0';

// 2026-10-24: where the current tab is remembered across a refresh
const CURRENT_TAB_KEY = 'psp_cre_current_tab';

// The scenario with the updates of a tab applied (the merge updateScenario always did; medical_configuration is merged one level deeper).
function mergeScenarioUpdates(prev, updates) {
  const next = { ...prev, ...updates, modified_at: new Date().toISOString() };
  if (updates.medical_configuration && prev?.medical_configuration) {
    next.medical_configuration = {
      ...prev.medical_configuration,
      ...updates.medical_configuration,
    };
  }
  return next;
}

function migrateScenario(scenario) {
  const sv = scenario.schema_version || '3.0.0';
  if (sv === CURRENT_SCHEMA_VERSION) return scenario;
  // 3.0.0 → 3.1.0: V4 fields (buildup_config, mroe_config matrix, CoA slots) are
  // all optional with null/undefined defaults and guarded by ?. at every read site.
  // No structural mutations needed — only stamp the new schema_version.
  const migrated = { ...scenario, schema_version: CURRENT_SCHEMA_VERSION };
  // eslint-disable-next-line no-console
  console.info(`[PSP/CRE] Schema migrated ${sv} \u2192 ${CURRENT_SCHEMA_VERSION}`);
  return migrated;
}

function App() {
  // V4-005: Language switcher
  const { t } = useTranslation();
  // With reload-based switching, uiLanguage never changes mid-session — derive directly.
  const uiLanguage = localStorage.getItem('psp_ui_language') || 'en';
  const switchLang = (lng) => {
    if (lng === uiLanguage) return;                // already in this language
    localStorage.setItem('psp_ui_language', lng);  // persist preference FIRST
    // React 19 + react-i18next useSyncExternalStore subscription does not reliably
    // re-render deep forwardRef components (Tab1, Tab2).  All scenario state is already
    // synced to localStorage via the auto-save effect, so a clean reload is safe and
    // guarantees every component initialises with the new language from i18n config.
    setTimeout(() => window.location.reload(), 50); // one tick for auto-save effects to flush
  };

  // Tab configuration — labels resolved via i18n so they update on language change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const TAB_CONFIG = React.useMemo(() => TAB_CONFIG_STATIC.map(tab => ({
    ...tab,
    label:      t(`nav.${tab.id}_label`),
    shortLabel: t(`nav.${tab.id}_short`),
    description: t(`nav.${tab.id}_desc`),
  })), [t]); // t changes identity when language switches → config re-computes

  // ── Phase 2: User role + identity ─────────────────────────────────────────
  // null = not yet identified (SplashScreen shown)
  // Resolved by SplashScreen.onResolved → stored here for the session lifetime
  const [userRole, setUserRole]       = useState(null);
  const [userSession, setUserSession] = useState({
    userName: '', trainerId: null, studentId: null,
    exerciseId: null, sessionCode: null,
  });
  const [isAdmin, setIsAdmin] = React.useState(false); // cloud storage: set after profile fetch

  // Co-organiser invite: if URL contains ?invite=TOKEN, store and auto-redeem on trainer login
  const [inviteNotification, setInviteNotification] = React.useState(null); // { type, msg } — errors only
  const [coOrgDialog, setCoOrgDialog] = React.useState({ open: false, exerciseName: '' }); // success dialog

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('invite');
    if (token) {
      sessionStorage.setItem('psp_pending_invite', token);
      // Clean the token out of the URL bar (no reload)
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const handleRoleResolved = React.useCallback(async (role, session) => {
    // P2-S3: when a student joins a room, pre-populate localStorage with the
    // trainer's scenario snapshot so all tabs mount with the correct data.
    if (role === 'STUDENT' && session.scenarioSnapshot) {
      StorageService.saveScenario('current', session.scenarioSnapshot);
    }
    setUserRole(role);
    setUserSession(prev => ({ ...prev, ...session }));

    // Co-organiser invite redemption — auto-redeem on trainer login if a token is waiting
    if (role === 'TRAINER') {
      const pendingToken = sessionStorage.getItem('psp_pending_invite');
      if (pendingToken) {
        sessionStorage.removeItem('psp_pending_invite');
        try {
          const result = await exerciseService.redeemCoOrganiserInvite(pendingToken);
          // BUG-S114-001: show a prominent dismissible dialog instead of the 9s auto-dismiss Alert
          setCoOrgDialog({ open: true, exerciseName: result.exercise_name || 'the exercise' });
        } catch (err) {
          // Errors stay as the sticky Alert (user needs to read the message carefully)
          setInviteNotification({ type: 'error', msg: err.message || 'Could not redeem invite.' });
          setTimeout(() => setInviteNotification(null), 9000);
        }
      }

      // Cloud storage: fetch (or auto-create) profile to determine isAdmin flag.
      // Non-blocking — failure is silently ignored; defaults to isAdmin=false.
      cloudStorageService.getProfile()
        .then(profile => { if (profile?.is_admin === true) setIsAdmin(true); })
        .catch(() => {});
    }
  }, []);

  // P2-S4: Trainer logout
  const [logoutDialogOpen,  setLogoutDialogOpen]  = React.useState(false);
  // P2-S6: Trainer account settings
  const [trainerSettingsOpen, setTrainerSettingsOpen] = React.useState(false);
  const handleLogoutConfirm = React.useCallback(async () => {
    try { await authService.logout(); } catch (_) { /* ignore — clear state anyway */ }
    setLogoutDialogOpen(false);
    setUserRole(null);
    setUserSession({ userName: '', trainerId: null, studentId: null, exerciseId: null, sessionCode: null });
  }, []);

  // readOnly: true when a student is inside an active exercise room
  // In P2-S1: always false — exercise room join implemented in P2-S3
  const readOnly = userRole === 'STUDENT' && !!userSession.exerciseId;

  // UserRoleContext value — stable shape, spread into Provider below
  const userRoleContextValue = React.useMemo(() => ({
    userRole:    userRole || 'GUEST',
    readOnly,
    exerciseId:  userSession.exerciseId,
    studentId:   userSession.studentId,
    trainerId:   userSession.trainerId,
    userName:    userSession.userName,
    sessionCode: userSession.sessionCode,
    isAdmin,
  }), [userRole, readOnly, userSession, isAdmin]);

  // ── Cloud scenario backup callbacks (TRAINER only) ────────────────────────
  // Passed to ScenarioToolbar; called after each Save. Fire-and-forget.
  const handleCloudSync = React.useCallback(async (scenarioData) => {
    const echelon  = scenarioData?.force?.echelon          || 'UNKNOWN';
    const standard = scenarioData?.force?.nationalStandard || 'UNKNOWN';
    const date     = new Date().toISOString().split('T')[0];
    await cloudStorageService.upsertScenario(scenarioData, `${standard}_${echelon}_${date}`);
  }, []);

  // Returns cloud result object for ScenarioToolbar to apply to scenario state.
  const handleCloudRestore = React.useCallback(async () => {
    return await cloudStorageService.getMyScenario(); // null | { name, data, saved_at }
  }, []);

  // Application state
  const [showWelcome, setShowWelcome] = useState(
    sessionStorage.getItem('welcomeAccepted') !== 'true');
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [mode, setMode] = useState(null);
  const [currentTab, setCurrentTab] = useState('tab1');
  const [completedTabs, setCompletedTabs] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // BUG-TAB1-DEFAULTS-001 FIX: incrementing this key remounts Tab1 so its
  // useState() initialisers re-run from the freshly-loaded scenario.force data.
  // BUG-LOAD-001: Extended to ALL tabs — Tab1 was the only tab with a key prop,
  // so file-load left Tabs 2-4 showing stale state (useState initialisers never re-ran).
  const [tab1ReloadKey, setTab1ReloadKey] = useState(0);
  const [allTabsReloadKey, setAllTabsReloadKey] = useState(0);
  // IMP-001: ORBAT locked after CoA A is saved — prevents force structure divergence
  const [orbatLocked, setOrbatLocked] = useState(false);

  // PHASE 4.7.4: Pre-save validation state (separate from per-tab validation)
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [saveValidationErrors, setSaveValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Snackbar for displaying save status and warnings
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ============================================================================
  // TAB REFS — for toolbar navigation flushState()
  // Each tab exposes flushState() via useImperativeHandle / forwardRef.
  // handleTabChange calls the active tab's flushState() before switching so
  // unsaved local state is silently persisted to scenario before unmounting.
  // ============================================================================
  const tab1Ref = useRef(null);
  const tab2Ref = useRef(null);
  const tab3Ref = useRef(null);
  const tab4Ref = useRef(null);
  const tab5Ref = useRef(null);
  const tab6Ref = useRef(null);
  const tab7Ref = useRef(null);
  const tab8Ref = useRef(null);

  // CRITICAL FIX: Prevent auto-save infinite loop
  const isSavingRef = useRef(false);
  const lastSavedHashRef = useRef(null);
  const isCalculatingRef = useRef(false); // NEW: Pause auto-save during calculations
  // 2026-10-24: the tab the planner is on is remembered across a refresh (key below). pendingTabRef holds the saved tab until it is open to
  // the planner (the tabs it requires are completed; Tab 1 completes a moment after the load, when it has restored its force), prevTabRef
  // is the tab last written.
  const pendingTabRef = useRef(null);
  const prevTabRef = useRef('tab1');
  // updates handed to updateScenario while the page closes (flushState of the active tab), applied to the scenario that is saved
  const capturedUpdatesRef = useRef(null);

  // Map tab id -> ref for clean lookup in handleTabChange
  const TAB_REFS = {
    tab1: tab1Ref,
    tab2: tab2Ref,
    tab3: tab3Ref,
    tab4: tab4Ref,
    tab5: tab5Ref,
    tab6: tab6Ref,
    tab7: tab7Ref,
    tab8: tab8Ref,
  };

  const handleSaveScenario = async () => {
    // PHASE 4.7.4: Pre-save validation gate
    const validation = validateResupplyAdequacy(scenario);

    if (validation.hasErrors) {
      // Block save, show error modal
      setSaveValidationErrors(validation.errors);
      setValidationModalOpen(true);
      return; // Stop here, don't save
    }

    if (validation.hasWarnings) {
      // Show warnings but allow save to continue
      setValidationWarnings(validation.warnings);
      const warningMessages = validation.warnings.map(w => w.message).join(', ');
      setSnackbar({
        open: true,
        message: `Warnings: ${warningMessages}`,
        severity: 'warning'
      });
      // Continue with save...
    }

    // Proceed with normal save logic
    try {
      // Strip calculation outputs before saving — they are stored separately
      // by storeBaseline() and must not be duplicated in the main scenario key.
      const scenarioToSave = { ...scenario };
      delete scenarioToSave.casualty_projections;
      delete scenarioToSave.calculation_results;
      const ok = StorageService.saveScenario('current', scenarioToSave);

      if (!ok) {
        setSnackbar({
          open: true,
          message: 'Save failed — browser storage limit reached. Delete unused scenarios or use a smaller force structure.',
          severity: 'error'
        });
        return;
      }

      // Show success message
      setSnackbar({
        open: true,
        message: 'Scenario saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Save failed:', error);
      setSnackbar({
        open: true,
        message: `Save failed: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Load saved state on mount
  // BUG-003 FIX: Version guard prevents stale v2.0 data from silently corrupting session
  // V4-DM-001: migrateScenario() backfills schema_version on v3.0 saves without it.
  useEffect(() => {
    const savedScenario = StorageService.loadScenario('current');
    if (savedScenario) {
      // Reject any scenario that is not v3.0 - clear it and start fresh
      if (savedScenario.version !== '3.0') {
        localStorage.clear();
        return;
      }

      // 2026-10-24: a save made before Brigade.toJSON wrote unit_type has brigades without it; fill it from brigade_type
      const readyScenario = repairScenarioForce(migrateScenario(savedScenario));
      setScenario(readyScenario);
      const completed = determineCompletedTabs(readyScenario);
      setCompletedTabs(completed);
      // 2026-10-24: the planner goes back to the tab they were on once that tab is open to them (see the effect below)
      try {
        const savedTab = localStorage.getItem(CURRENT_TAB_KEY);
        if (savedTab && savedTab !== 'tab1' && TAB_CONFIG_STATIC.some(t => t.id === savedTab)) pendingTabRef.current = savedTab;
      } catch (_e) { /* storage unavailable: start on Tab 1 */ }

      if (savedScenario.mode) {
        setMode(savedScenario.mode);
        setShowModeSelector(false);
      }
    }
  }, []);

  // 2026-10-24: open the saved tab as soon as the tabs it requires are completed (checked again whenever the completed tabs change)
  useEffect(() => {
    const pending = pendingTabRef.current;
    if (!pending) return;
    const cfg = TAB_CONFIG_STATIC.find(t => t.id === pending);
    if (cfg && cfg.requires.every(req => completedTabs.includes(req))) {
      pendingTabRef.current = null;
      setCurrentTab(pending);
    }
  }, [completedTabs]);

  // 2026-10-24: remember the current tab; a move of the planner cancels a restore that is still waiting
  useEffect(() => {
    if (prevTabRef.current === currentTab) return;
    prevTabRef.current = currentTab;
    pendingTabRef.current = null;
    try { localStorage.setItem(CURRENT_TAB_KEY, currentTab); } catch (_e) { /* storage unavailable */ }
  }, [currentTab]);

  // Auto-save scenario when it changes
  useEffect(() => {
    if (!scenario || scenario.version !== '3.0') {
      return;
    }
    
    // Prevent concurrent saves
    if (isSavingRef.current) {
      return;
    }
    
    // Don't save during calculations (prevents loop when calculator updates scenario)
    if (isCalculatingRef.current) {
      return;
    }
    
    // CRITICAL: Hash ONLY user-input fields, NOT calculation results
    // This prevents Tab 4's bed occupancy/MDOS from triggering saves
    try {
      const scenarioHash = JSON.stringify({
        // Force structure (user built)
        force_echelon: scenario.force?.echelon,
        force_brigades_count: scenario.force?.forceBuilderResult?.brigades?.length,
        force_brigade_ids: scenario.force?.forceBuilderResult?.brigades?.map(b => b.id),
        
        // Operation params (user configured)
        op_duration: scenario.operation_parameters?.duration_days,
        op_type: scenario.operation_parameters?.operation_type,
        op_posture: scenario.operation_parameters?.operational_posture,
        op_threat: scenario.operation_parameters?.threat_level,
        monte_carlo_enabled: scenario.operation_parameters?.monte_carlo_config?.enabled,
        
        // Medical config (user set)
        medical_configured: scenario.medical_configuration?.configured,
        
        // Logistics (user set)
        logistics_configured: scenario.logistics?.configured
        
        // EXPLICITLY EXCLUDE:
        // - casualty_projections (calculation results)
        // - monte_carlo_modifiers (simulator artifacts)
        // - bed occupancy (Tab 4 calculation)
        // - MDOS analysis (Tab 4 calculation)
        // - Any other calculation results
      });
      
      // FIX: Also check if calculator results changed (even if user inputs didn't)
      // This ensures Tab4's calculator results get saved to localStorage
      const hasCalculatorResults = !!scenario.casualty_projections?.version;
      const calculatorTimestamp = scenario.calculated_at;
      
      // Skip if user hasn't changed anything AND calculator hasn't run
      if (scenarioHash === lastSavedHashRef.current && !hasCalculatorResults) {
        return;
      }
      
      // Force save if calculator just ran (new timestamp)
      const shouldForceSave = hasCalculatorResults && calculatorTimestamp !== lastSavedHashRef.calculatorTimestamp;
      
      if (!shouldForceSave && scenarioHash === lastSavedHashRef.current) {
        return;
      }
      
      // BUG-FF-012 FIX: Strip calculation outputs before saving to psp_cre_current_scenario_v2.
      // casualty_projections / calculation_results are written by Tab 5's onUpdate() and merged
      // into scenario state. Including them in the auto-save regularly exceeds the localStorage
      // quota (Safari 5 MB, Firefox ~10 MB), blocking psp_calculation_baseline from being written
      // and breaking Tab 6 COA baseline load. These fields are preserved separately by
      // storeBaseline() in psp_calculation_baseline and must not be duplicated here.
      isSavingRef.current = true;
      const scenarioToSave = { ...scenario };
      delete scenarioToSave.casualty_projections;
      // Preserve tab5 completion flag; strip heavy projection data only.
      // Deleting the entire object causes calculation_results.completed to be
      // lost on reload → determineCompletedTabs() marks Tab 5 incomplete →
      // Tab 6 + 7 become inaccessible after every browser refresh.
      if (scenarioToSave.calculation_results) {
        scenarioToSave.calculation_results = {
          completed: !!scenarioToSave.calculation_results.completed,
        };
      }
      StorageService.saveScenario('current', scenarioToSave);
      lastSavedHashRef.current = scenarioHash;
      if (hasCalculatorResults) {
        lastSavedHashRef.calculatorTimestamp = calculatorTimestamp;
      }
    } catch (error) {
      console.error('App.jsx AUTO-SAVE ERROR:', error);
    } finally {
      isSavingRef.current = false;
    }
  }, [scenario]);

  // v2.9 FIX: Save on page refresh/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (scenario && scenario.version === '3.0') {
        // 2026-10-24: the edits typed in the active tab and not yet passed to the scenario (Tab 2 buffers them until a tab switch or Save &
        // Continue) are flushed now: the updates the tab hands over are captured and applied to the scenario that is saved (React would not
        // have applied them before the page goes).
        let current = scenario;
        capturedUpdatesRef.current = [];
        try {
          const activeRef = TAB_REFS[currentTab];
          if (activeRef?.current?.flushState) activeRef.current.flushState();
        } catch (_e) { /* a failing flush must not stop the save of what is there */ }
        const captured = capturedUpdatesRef.current || [];
        capturedUpdatesRef.current = null;
        captured.forEach((u) => { current = mergeScenarioUpdates(current, u); });
        const scenarioToSave = { ...current };
        delete scenarioToSave.casualty_projections;
        // Same fix as auto-save: keep completed flag, drop heavy data.
        if (scenarioToSave.calculation_results) {
          scenarioToSave.calculation_results = {
            completed: !!scenarioToSave.calculation_results.completed,
          };
        }
        StorageService.saveScenario('current', scenarioToSave);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, currentTab]);

  // IMP-001: Listen for CoA A saved → lock ORBAT; listen for unlock event → release
  useEffect(() => {
    const handleCoaSaved = (e) => {
      if (e?.detail?.slot === 1) setOrbatLocked(true);
    };
    const handleOrbatUnlocked = () => setOrbatLocked(false);
    window.addEventListener('psp:coaSaved', handleCoaSaved);
    window.addEventListener('psp:orbatUnlocked', handleOrbatUnlocked);
    return () => {
      window.removeEventListener('psp:coaSaved', handleCoaSaved);
      window.removeEventListener('psp:orbatUnlocked', handleOrbatUnlocked);
    };
  }, []);

  // Determine which tabs are completed
  const determineCompletedTabs = (scenarioData) => {
    const completed = [];
    // BUG-TAB1-COMPLETION-001: require actual force structure (brigades built),
    // not just echelon selection. Prevents default-loaded scenarios from showing
    // Tab 1 complete when forceBuilderResult is null.
    // 2026-10-24: a BRIGADE or BATTALION force built with the quick build keeps its unit as the singular `brigade` / `battalion` and an EMPTY
    // `brigades` array (the calculator reads the singular too). Counting only brigades[] made Tab 1 incomplete after every refresh and locked
    // Tabs 2 to 5 until Tab 1 was saved again.
    if (
      scenarioData.force &&
      scenarioData.force.echelon &&
      (scenarioData.force.forceBuilderResult?.brigades?.length > 0 ||
        scenarioData.force.forceBuilderResult?.brigade ||
        scenarioData.force.forceBuilderResult?.battalion)
    ) completed.push('tab1');
    if (scenarioData.operation_parameters && scenarioData.operation_parameters.configured) completed.push('tab2');
    if (scenarioData.medical_configuration && scenarioData.medical_configuration.configured) completed.push('tab3');
    if (scenarioData.logistics && scenarioData.logistics.configured) completed.push('tab4');
    if (scenarioData.calculation_results && scenarioData.calculation_results.completed) completed.push('tab5');
    if (scenarioData.coa_analysis && scenarioData.coa_analysis.configured) completed.push('tab6');
    return completed;
  };

  // Handle welcome screen completion
  const handleWelcomeComplete = () => {
    sessionStorage.setItem('welcomeAccepted', 'true');
    setShowWelcome(false);
    setShowModeSelector(true);
  };

  // Handle mode selection
  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    setShowModeSelector(false);

    const newScenario = {
      version: '3.0',
      schema_version: CURRENT_SCHEMA_VERSION, // V4-DM-001
      scenario_type: 'HIERARCHICAL',
      mode: selectedMode,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
      author: 'Dr. Tatar Radu MD',
      force: null,
      operation_parameters: null,
      medical_configuration: null,
      logistics: null,
      calculation_results: null,
      reports_generated: false,
      coa_analysis: null
    };

    setScenario(newScenario);
    setCurrentTab('tab1');
  };

  // Check if a tab is accessible
  const isTabAccessible = (tabId) => {
    const tabConfig = TAB_CONFIG.find(t => t.id === tabId);
    if (!tabConfig) return false;
    return tabConfig.requires.every(reqTab => completedTabs.includes(reqTab));
  };

  // Check if a tab is completed
  const isTabCompleted = (tabId) => {
    return completedTabs.includes(tabId);
  };

  // Handle tab change from toolbar stepper
  // BUG-066 FIX: Tab content uses CSS display switching (always mounted),
  // so switching tabs here never loses internal component state.
  const handleTabChange = (tabId) => {
    if (isTabAccessible(tabId)) {
      // Flush active tab's unsaved local state to scenario before switching.
      // Each tab exposes flushState() via forwardRef/useImperativeHandle.
      // This is a synchronous, silent save — no validation, no navigation side-effects.
      const activeRef = TAB_REFS[currentTab];
      if (activeRef?.current?.flushState) {
        activeRef.current.flushState();
      }
      // ENH-WORKFLOW-003: Track navigation method for Tab 5 UX notice.
      // 'direct' = toolbar jump (flushed but not sequentially validated).
      // 'sequential' = set by markTabComplete(tab4) flow.
      if (tabId === 'tab5') {
        try { sessionStorage.setItem('psp_tab5_nav_method', 'direct'); } catch (_e) { /* noop */ }
      }
      setCurrentTab(tabId);
      // v2.8 FIX: Scroll to top when changing tabs - robust approach
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  };

  /**
   * Mark tab as complete and advance to next tab
   *
   * FIX v2.7: Navigation now happens ALWAYS, not just on first completion
   */
  const markTabComplete = (tabId) => {
    // Add to completed tabs if not already there
    if (!completedTabs.includes(tabId)) {
      const newCompleted = [...completedTabs, tabId];
      setCompletedTabs(newCompleted);
    }

    // Navigation ALWAYS happens (moved outside the if block)
    const currentTabIndex = TAB_CONFIG.findIndex(t => t.id === tabId);
    if (currentTabIndex < TAB_CONFIG.length - 1) {
      const nextTab = TAB_CONFIG[currentTabIndex + 1];
      // ENH-WORKFLOW-003: When tab4 completes and advances to tab5, mark as sequential navigation.
      if (nextTab.id === 'tab5') {
        try { sessionStorage.setItem('psp_tab5_nav_method', 'sequential'); } catch (_e) { /* noop */ }
      }
      setTimeout(() => {
        setCurrentTab(nextTab.id);
        // v2.8 FIX: Scroll to top when advancing to next tab - robust approach
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  };

  // Mark tab as incomplete
  const markTabIncomplete = (tabId) => {
    const tabIndex = TAB_CONFIG.findIndex(t => t.id === tabId);
    const tabsToRemove = TAB_CONFIG.slice(tabIndex).map(t => t.id);
    setCompletedTabs(completedTabs.filter(t => !tabsToRemove.includes(t)));
  };

  // Update scenario data
  // CRIT-007 FIX: Each tab passes only its own domain data.
  // App.jsx spreads over prev to avoid overwriting other tabs.
  // BUG-S97-001 FIX: deep-merge medical_configuration so Tab2 partial updates
  // (mtf_hierarchy, echelon, facility_count) never wipe Tab3's full config
  // (per_facility_evacuation_assets, evacuation_assets, blood_config, etc.).
  // Tab3 still wins on all fields it provides — merge means "add missing fields only
  // from prev; Tab3's own keys overwrite prev because Tab3 always provides them".
  const updateScenario = (updates) => {
    if (capturedUpdatesRef.current) capturedUpdatesRef.current.push(updates);   // the page is closing: see handleBeforeUnload
    setScenario(prev => mergeScenarioUpdates(prev, updates));
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    return Math.round((completedTabs.length / TAB_CONFIG.length) * 100);
  };

  // Get step icon
  const getStepIcon = (tabId) => {
    if (isTabCompleted(tabId)) return <CheckCircleIcon color="success" />;
    else if (!isTabAccessible(tabId)) return <LockIcon color="disabled" />;
    else if (currentTab === tabId) return <RadioButtonUncheckedIcon color="primary" />;
    else return <RadioButtonUncheckedIcon color="disabled" />;
  };

  // Phase 2: Render SplashScreen until user identifies themselves
  // SplashScreen auto-skips if a valid Supabase session exists (trainer remember-me)
  if (userRole === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SplashScreen onResolved={handleRoleResolved} />
      </ThemeProvider>
    );
  }

  // Render welcome screen
  if (showWelcome) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <WelcomeScreen onComplete={handleWelcomeComplete} />
      </ThemeProvider>
    );
  }

  // Render mode selector
  if (showModeSelector) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ModeSelector onModeSelect={handleModeSelect} />
      </ThemeProvider>
    );
  }

  // Render main application
  return (
    <UserRoleContext.Provider value={userRoleContextValue}>
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

          {/* Co-organiser invite error notification (auto-dismisses after 9s) */}
          {inviteNotification && (
            <Alert
              severity={inviteNotification.type}
              onClose={() => setInviteNotification(null)}
              sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1400 }}
            >
              {inviteNotification.msg}
            </Alert>
          )}

          {/* BUG-S114-001: Co-organiser invite success — prominent dialog, not auto-dismiss */}
          <Dialog
            open={coOrgDialog.open}
            maxWidth="sm"
            fullWidth
            onClose={() => setCoOrgDialog(p => ({ ...p, open: false }))}
          >
            <DialogTitle sx={{ pb: 1 }}>
              ✓ Co-organiser access granted
            </DialogTitle>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                You have been added as co-organiser of <strong>"{coOrgDialog.exerciseName}"</strong>.
              </Alert>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                What this means:
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="The exercise now appears in your Tab 7 exercise list." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="You can open it, run injects, and view student responses." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="The lead trainer remains the exercise owner." />
                </ListItem>
              </List>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => setCoOrgDialog(p => ({ ...p, open: false }))}
              >
                Got it — take me to Tab 7
              </Button>
            </DialogActions>
          </Dialog>

          {/* Global Scenario Toolbar */}
          <ScenarioToolbar
            scenario={scenario}
            onScenarioUpdate={updateScenario}
            onSave={handleSaveScenario}
            onScenarioLoaded={(loadedScenario) => {
              // BUG-LOAD-001: remount ALL tabs so useState() initialisers re-run
              setTab1ReloadKey(k => k + 1);
              setAllTabsReloadKey(k => k + 1);
              // Also re-derive completedTabs from the loaded data so tabs unlock correctly
              if (loadedScenario) setCompletedTabs(determineCompletedTabs(loadedScenario));
            }}
            onReset={() => {
              // BUG-RESET-001 FIX: Remove all PSP/CRE state keys but preserve the
              // Supabase auth token. The token is stored under 'psp_cre_trainer_session'
              // (see supabaseClient.js storageKey) which starts with 'psp_' — so it must
              // be explicitly excluded from the sweep to keep the trainer logged in.
              const AUTH_KEY = 'psp_cre_trainer_session';
              Object.keys(localStorage)
                .filter(k => k !== AUTH_KEY && (k.startsWith('psp_') || k.startsWith('PSP_')))
                .forEach(k => localStorage.removeItem(k));
              window.location.reload();
            }}
            mode={mode}
            onCloudSync={userRole === 'TRAINER' ? handleCloudSync : undefined}
            onCloudRestore={userRole === 'TRAINER' ? handleCloudRestore : undefined}
          />

          {/* V4-005: Language selector + P2-S4 trainer identity/logout bar */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 0.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', gap: 1 }}>
            {/* Trainer identity chip + logout — prevents silent account sharing on shared PCs */}
            {userRole === 'TRAINER' && (
              <>
                <Chip
                  label={userSession.userName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<LockIcon sx={{ fontSize: '0.8rem !important' }} />}
                  sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                  onClick={() => setTrainerSettingsOpen(true)}
                  title="Account settings"
                />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => setLogoutDialogOpen(true)}
                  sx={{ py: 0.1, px: 1, fontSize: '0.7rem', minWidth: 0 }}
                >
                  {t('nav.logout_btn')}
                </Button>
              </>
            )}
            <Button
              size="small"
              variant={uiLanguage === 'en' ? 'contained' : 'outlined'}
              onClick={() => switchLang('en')}
              sx={{ minWidth: 40, mr: 0.5, py: 0.25, fontSize: '0.75rem' }}
            >
              EN
            </Button>
            <Button
              size="small"
              variant={uiLanguage === 'ro' ? 'contained' : 'outlined'}
              onClick={() => switchLang('ro')}
              sx={{ minWidth: 40, py: 0.25, fontSize: '0.75rem' }}
            >
              RO
            </Button>
          </Box>

          {/* P2-S4: Logout confirmation dialog */}
          <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>{t('nav.logout_title')}</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                {t('nav.logout_confirm', { name: userSession.userName })}
              </Typography>
              <Typography variant="body2" color="warning.dark" sx={{ mt: 1, p: 1, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                {t('nav.logout_warning')}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLogoutDialogOpen(false)}>{t('nav.cancel')}</Button>
              <Button onClick={handleLogoutConfirm} color="error" variant="contained">{t('nav.logout_btn')}</Button>
            </DialogActions>
          </Dialog>

          {/* P2-S6: Trainer account settings (name + password) */}
          <TrainerSettings
            open={trainerSettingsOpen}
            onClose={() => setTrainerSettingsOpen(false)}
            currentName={userSession.userName}
            onNameUpdated={(newName) => {
              setUserSession(prev => ({ ...prev, userName: newName }));
              setTrainerSettingsOpen(false);
            }}
          />

          {/* Force Rebuild Warning — shown when a loaded scenario has force.echelon
              but no forceBuilderResult. Covers: old saves (pre-forceBuilder schema),
              freshly loaded defaults, and files from an earlier app version.
              Disappears automatically once the user builds in Tab 1.            */}
          {/* BUG-T01 fix: only show when scenario has been partially configured (has Tab 2+ data)
              — prevents false positive on brand-new / freshly-loaded default scenarios */}
          {scenario?.force?.echelon && !scenario?.force?.forceBuilderResult &&
           (scenario?.operations || scenario?.medical_configuration || scenario?.logistics) && (
            <Alert
              severity="warning"
              sx={{ borderRadius: 0, borderBottom: '1px solid', borderColor: 'warning.light' }}
            >
              <AlertTitle>{t('nav.force_rebuild_title')}</AlertTitle>
              {t('nav.force_rebuild_body')}
            </Alert>
          )}

          {/* Validation Warnings Alert - Shows when scenario saved with warnings */}
          {validationWarnings.length > 0 && (
            <Box sx={{ width: '100%', bgcolor: 'background.paper', px: 3, pt: 2 }}>
              <Alert
                severity="warning"
                onClose={() => setValidationWarnings([])}
                sx={{ mb: 0 }}
              >
                <AlertTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    {t('nav.scenario_saved_warnings_title')}
                  </Box>
                </AlertTitle>
                <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                  {t('nav.scenario_saved_warnings_body')}
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {validationWarnings.map((warning, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <WarningIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={warning.message}
                        secondary={warning.fix}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            </Box>
          )}

          {/* Progress Bar */}
          <Box sx={{ width: '100%', bgcolor: 'background.paper', py: 2, px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 200 }}>
                {t('nav.progress_bar', { completed: completedTabs.length, total: TAB_CONFIG.length, pct: calculateProgress() })}
              </Typography>
              <Box sx={{ flexGrow: 1, mx: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={calculateProgress()}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>

            {/* Step Navigation */}
            <Stepper activeStep={TAB_CONFIG.findIndex(t => t.id === currentTab)} nonLinear alternativeLabel>
              {TAB_CONFIG.map((tab) => (
                <Step key={tab.id} completed={isTabCompleted(tab.id)}>
                  <StepButton
                    onClick={() => handleTabChange(tab.id)}
                    disabled={!isTabAccessible(tab.id)}
                    icon={getStepIcon(tab.id)}
                  >
                    <StepLabel>{tab.shortLabel}</StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Main Content */}
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
            <Paper elevation={3} sx={{ p: 3, minHeight: '600px' }}>

              {/* Current tab description */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>{TAB_CONFIG.find(t => t.id === currentTab)?.label}</strong>
                {' - '}
                {TAB_CONFIG.find(t => t.id === currentTab)?.description}
              </Alert>

              {/* Validation errors */}
              {validationErrors[currentTab] && validationErrors[currentTab].length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <strong>{t('nav.please_fix_errors')}</strong>
                  <ul>
                    {validationErrors[currentTab].map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* mode guard: all tabs mount together only AFTER mode is confirmed,
                  ensuring every tab receives the correct mode prop (e.g. 'expert')
                  on its very first render. BUG-066 fix (display:none/block) is
                  preserved - tabs stay mounted permanently once mode is set. */}
              {mode && (
                <>
                  <Box sx={{ display: currentTab === 'tab1' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab1ForceComposition
                      key={tab1ReloadKey}
                      ref={tab1Ref}
                      scenario={scenario}
                      mode={mode}
                      orbatLocked={orbatLocked}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab1')}
                      onEdit={() => markTabIncomplete('tab1')}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab1: errors }))}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab2' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab2Operations
                      key={allTabsReloadKey}
                      ref={tab2Ref}
                      scenario={scenario}
                      mode={mode}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab2')}
                      onEdit={() => {
                        markTabIncomplete('tab2');
                        setCurrentTab('tab1');
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab2: errors }))}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab3' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab3Medical
                      key={allTabsReloadKey}
                      ref={tab3Ref}
                      scenario={scenario}
                      mode={mode}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab3')}
                      onEdit={() => {
                        markTabIncomplete('tab3');
                        setCurrentTab('tab2');
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab3: errors }))}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab4' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab4Logistics
                      key={allTabsReloadKey}
                      ref={tab4Ref}
                      scenario={scenario}
                      mode={mode}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab4')}
                      onEdit={() => {
                        markTabIncomplete('tab4');
                        setCurrentTab('tab3');
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab4: errors }))}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab5' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab5Reports
                      ref={tab5Ref}
                      scenario={scenario}
                      mode={mode}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab5')}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab5: errors }))}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab6' ? 'block' : 'none' }}>
                    {readOnly && <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
                      {t('nav.exercise_readonly')}
                    </Alert>}
                    <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                    <Tab6COAAnalysis
                      ref={tab6Ref}
                      scenario={scenario}
                      mode={mode}
                      isVisible={currentTab === 'tab6'}
                      onUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab6')}
                      onValidationChange={(errors) => setValidationErrors(prev => ({ ...prev, tab6: errors }))}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                      onLoadApprovedPlan={(snapshotData) => {
                        updateScenario(snapshotData);
                        setTab1ReloadKey(k => k + 1);
                        setAllTabsReloadKey(k => k + 1);
                      }}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ display: currentTab === 'tab7' ? 'block' : 'none' }}>
                    <Tab7Exercise
                      ref={tab7Ref}
                      scenario={scenario}
                      mode={mode}
                      onScenarioUpdate={updateScenario}
                      onComplete={() => markTabComplete('tab7')}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                    />
                  </Box>

                  <Box sx={{ display: currentTab === 'tab8' ? 'block' : 'none' }}>
                    <Tab8Debrief
                      ref={tab8Ref}
                      scenario={scenario}
                      onComplete={() => markTabComplete('tab8')}
                      onNavigate={(tabIndex) => {
                        setCurrentTab(`tab${tabIndex + 1}`);
                        window.scrollTo(0, 0);
                        document.documentElement.scrollTop = 0;
                      }}
                    />
                  </Box>
                </>
              )}

            </Paper>
          </Container>

          {/* Validation Modal - Blocks save when critical resupply errors detected */}
          <Dialog
            open={validationModalOpen}
            onClose={() => setValidationModalOpen(false)}
            maxWidth="md"
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                Cannot Save Scenario - Critical Issues Detected
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Resupply Inadequacy Detected</AlertTitle>
                <Typography variant="body2">
                  Your scenario has critical blood supply shortages that would result in stockout during operations.
                  This scenario cannot be saved until resupply is adequate.
                </Typography>
              </Alert>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Issues Found:
              </Typography>
              <List>
                {saveValidationErrors.map((error, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={error.message}
                      secondary={error.fix}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setValidationModalOpen(false)}>
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  // BUG-004 FIX: Use string ID 'tab4', not integer 3
                  setCurrentTab('tab4');
                  setValidationModalOpen(false);
                  // Scroll to resupply section
                  setTimeout(() => {
                    const section = document.getElementById('resupply-section');
                    if (section) {
                      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                Fix in Tab 4
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for displaying validation warnings and save status */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 3,
              mt: 'auto',
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              PSP/CRE Tool v3.0 — Educational Tool for Military Medical Training | Dr. Tatar Radu MD
            </Typography>
            <Typography variant="caption" color="text.disabled" align="center" display="block" sx={{ mt: 0.5 }}>
              Contact developer:{' '}
              <a href="mailto:psp.cre.tool@gmail.com" style={{ color: 'inherit' }}>
                psp.cre.tool@gmail.com
              </a>
            </Typography>
          </Box>

        </Box>
      </ThemeProvider>
    </ErrorBoundary>
    </UserRoleContext.Provider>
  );
}

export default App;
